/**
 * Theme policy (pure; no DOM, no React).
 *
 * Owner decision, 4 Oct 2026: the dashboard is dark only. The stored `app-theme` preference is
 * kept exactly as it is: it is never written except by an explicit user action (setTheme /
 * toggleTheme, which nothing renders while DARK_ONLY is on) and never removed. Storage is passed
 * as a getter, so a browser that throws on reading the localStorage global (Safari with storage
 * blocked) falls back to dark instead of crashing the mount.
 */

export type Theme = 'dark' | 'light';

/** Owner decision (4 Oct 2026): show the dark theme only. */
export const DARK_ONLY = true;

export const STORAGE_KEY = 'app-theme';

/** The subset of Storage the policy uses. */
export type ThemeStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** Reads the stored preference; 'light' only when the value is exactly 'light'. Never writes. */
export function readPreference(storage: () => ThemeStorage): Theme {
  try {
    return storage().getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/** The theme that is shown for a stored preference. */
export function displayedTheme(preference: Theme, darkOnly: boolean): Theme {
  return darkOnly ? 'dark' : preference;
}

/** Writes the preference. Called only from an explicit user action. */
export function persistPreference(storage: () => ThemeStorage, theme: Theme): void {
  try {
    storage().setItem(STORAGE_KEY, theme);
  } catch {
    // storage unavailable (private mode) — the theme still applies for the session
  }
}
